import { omit, map, filter, reject } from 'lodash';
import { createSelector } from 'reselect';
import { beginTask, endTask } from 'redux-nprogress';
import { selectParams } from './location.selectors';
import { goToView } from '../../app/routes.global';
import { addTracking, changeTracking, deleteTracking } from '../actions/tracking.actions';
import { CUSTOM_LEAGUE_VIEW } from '../../app/views/Custom/module/custom';

// ------------------------------------
// Selectors
// ------------------------------------
export const STORE_KEY = 'formulas';
export const selectFormulasState = state => state[STORE_KEY];
export const selectFormula = (state, id) => selectFormulasState(state).formulas[id];
export const selectFormulas = state => Object.values(selectFormulasState(state).formulas);
// TODO: Until the default key bug where the id does not update when deleting a default formula is
// fixed on the api, I am returning the '1' key formula when a default is not found.
export const selectDefaultFormula = state => {
  const formulasState = selectFormulasState(state);
  return formulasState.formulas[formulasState.defaultFormula] || formulasState.formulas[1];
};

// all non-custom (public) formulas from the api will have a user_id of 0
export const selectCustomFormulas = createSelector(
  selectFormulas,
  formulas => formulas.filter(formula => formula.user_id !== 0),
);

export const selectFormulasForDropdown = createSelector(
  selectFormulas,
  formulas => formulas.sort((a, b) => a.user_id - b.user_id).map(({ id, name }) => ({
    key: id,
    value: id,
    text: name,
  })),
);
export const selectFormulaOptions = state => selectFormulasState(state).options;

// ------------------------------------
// Reducer
// ------------------------------------
const actions = {};
const initialState = {
  formulas: {},
  defaultFormula: 2,
  options: {},
  error: null,
  hasLoaded: false,
};

const formulasReducer = (state = initialState, action) => {
  const handler = actions[action.type];

  return handler ? handler(state, action) : state;
};

export default formulasReducer;

// ====================================
// Actions/Handlers
// ====================================

// ------------------------------------
// Receive new formulas and options
// ------------------------------------
export const LOAD_FORMULAS_REQUEST = `${STORE_KEY}/LOAD_FORMULAS_REQUEST`;
export const LOAD_FORMULAS_SUCCESS = `${STORE_KEY}/LOAD_FORMULAS_SUCCESS`;
export const LOAD_FORMULAS_ERROR = `${STORE_KEY}/LOAD_FORMULAS_ERROR`;

export const loadFormulasRequest = () => ({ type: LOAD_FORMULAS_REQUEST });
export const loadFormulasSuccess = ({ formulas, defaultFormula }) => ({
  type: LOAD_FORMULAS_SUCCESS,
  payload: { formulas, defaultFormula },
});
export const loadFormulasError = error => ({
  type: LOAD_FORMULAS_ERROR,
  payload: { error },
});

actions[LOAD_FORMULAS_SUCCESS] = (state, { payload: { defaultFormula, formulas } }) => ({ ...state,
  defaultFormula,
  formulas,
  hasLoaded: true,
});
actions[LOAD_FORMULAS_ERROR] = (state, { payload: { error } }) => ({ ...state,
  error,
});

export const loadFormulas = async (dispatch, getState, { api }) => {
  try {
    dispatch(loadFormulasRequest());
    const { formulas, default_formula_id: defaultFormula } = await api.formula.getAll();

    return dispatch(loadFormulasSuccess({ formulas, defaultFormula }));
  } catch (error) {
    return dispatch(loadFormulasError(error));
  }
};

// ------------------------------------
// Set a new default formula
// ------------------------------------
export const SET_DEFAULT_FORMULA_REQUEST = `${STORE_KEY}/SET_DEFAULT_FORMULA_REQUEST`;
export const SET_DEFAULT_FORMULA_SUCCESS = `${STORE_KEY}/SET_DEFAULT_FORMULA_SUCCESS`;
export const SET_DEFAULT_FORMULA_ERROR = `${STORE_KEY}/SET_DEFAULT_FORMULA_ERROR`;

export const setDefaultFormulaRequest = id => ({
  type: SET_DEFAULT_FORMULA_REQUEST,
  payload: { id },
});
export const setDefaultFormulaSuccess = id => ({
  type: SET_DEFAULT_FORMULA_SUCCESS,
  payload: { id },
});
export const setDefaultFormulaError = (error, originalId) => ({
  type: SET_DEFAULT_FORMULA_ERROR,
  payload: { error, originalId },
});

actions[SET_DEFAULT_FORMULA_REQUEST] = (state, { payload }) => ({ ...state,
  defaultFormula: payload.id,
});
actions[SET_DEFAULT_FORMULA_ERROR] = (state, { payload }) => ({ ...state,
  defaultFormula: payload.originalId,
  error: payload.error,
});

export const setDefaultFormula = id => async (dispatch, getState, { api }) => {
  const currentId = selectFormulasState(getState()).defaultFormula;

  try {
    dispatch(setDefaultFormulaRequest(id));
    await api.formula.setDefault(id);

    dispatch(setDefaultFormulaSuccess());
  } catch (error) {
    console.error(error);
    dispatch(setDefaultFormulaError(error, currentId));
  }
};

// ------------------------------------
// Delete an existing Formula
// ------------------------------------
export const DELETE_FORMULA_REQUEST = `${STORE_KEY}/DELETE_FORMULA_REQUEST`;
export const DELETE_FORMULA_SUCCESS = `${STORE_KEY}/DELETE_FORMULA_SUCCESS`;
export const DELETE_FORMULA_ERROR = `${STORE_KEY}/DELETE_FORMULA_ERROR`;

export const deleteFormulaRequest = id => ({
  type: DELETE_FORMULA_REQUEST,
  payload: { id },
});
export const deleteFormulaSuccess = id => ({
  type: DELETE_FORMULA_SUCCESS,
  payload: { id },
});
export const deleteFormulaError = error => ({
  type: DELETE_FORMULA_ERROR,
  payload: { error },
});

actions[DELETE_FORMULA_ERROR] = (state, { payload }) => ({ ...state,
  error: payload.error,
});
actions[DELETE_FORMULA_SUCCESS] = (state, { payload }) => ({ ...state,
  formulas: omit(state.formulas, payload.id),
});

export const deleteFormula = id => async (dispatch, getState, { api }) => {
  try {
    dispatch(deleteFormulaRequest(id));
    await api.formula.deleteFormula({ id });

    dispatch(deleteFormulaSuccess(id));
  } catch (error) {
    console.error(error);
    dispatch(deleteFormulaError(error));
  }
};

// ------------------------------------
// Save League Formula
// ------------------------------------
export const SAVE_FORMULA_REQUEST = `${STORE_KEY}/SAVE_FORMULA_REQUEST`;
export const SAVE_FORMULA_SUCCESS = `${STORE_KEY}/SAVE_FORMULA_SUCCESS`;
export const SAVE_FORMULA_ERROR = `${STORE_KEY}/SAVE_FORMULA_ERROR`;

export const saveFormulaRequest = formula => ({
  type: SAVE_FORMULA_REQUEST,
  payload: { formula },
});
export const saveFormulaSuccess = (newId, formula) => ({
  type: SAVE_FORMULA_SUCCESS,
  payload: { newId, formula },
});
export const saveFormulaError = error => ({
  type: SAVE_FORMULA_ERROR,
  payload: { error },
});

// TODO: No idea why this does not work, causes a bunch of errors...
// Currently when saving we change routes anyways so the data is loaded and it's fine,
// but if we save and DON'T reload the data, we have to fix this (such that it updates in place)

// actions[SAVE_FORMULA_SUCCESS] = (state, { payload }) => ({ ...state,
//   formulas: { ...state.formulas,
//     [payload.newId]: cloneDeep(payload.formula),
//   },
// });
actions[SAVE_FORMULA_ERROR] = (state, { payload }) => ({ ...state,
  error: payload.error,
});

export const saveFormula = formula => async (dispatch, getState, { api }) => {
  try {
    dispatch(beginTask());
    dispatch(saveFormulaRequest(formula));

    const state = getState();
    const { id, edit } = state;
    const { shouldDuplicateTracking, updatedTracking, trackings, leagueId } = state.customLeague;
    const response = edit
      ? await api.formula.update({ id, formula })
      : await api.formula.createNew({ formula });

    if (shouldDuplicateTracking) {      
      const originalPlayers = [];
      const originalTracking = [];
      if (trackings.length) {
        trackings.forEach(({ player_id, formulas }) => {
          originalPlayers.push({ player_id });
          originalTracking.push({
            player_id,
            formula_ids: reject(formulas, ({ formula_id }) => formula_id === leagueId),
          });
        });
      }

      const trackingToSave = [];
      if (updatedTracking && updatedTracking.length) {
        updatedTracking.forEach(({ player_id }) => {
          trackingToSave.push({
            player_id,
            formula_ids: [response.formula.id],
          });
        });
      }

      await Promise.all([
        // delete each player
        originalPlayers.length && dispatch(deleteTracking(originalPlayers)),
        // add the old tracking list
        originalTracking.length && dispatch(changeTracking(originalTracking)),
        // add the new trackings
        updatedTracking.length && dispatch(changeTracking(updatedTracking)),
      ]);

      // dispatch(changeTracking(trackingToSave));
    }

    dispatch(saveFormulaSuccess(response.formula.id, formula));
    dispatch(goToView(CUSTOM_LEAGUE_VIEW));
  } catch (error) {
    console.error(error);
    dispatch(saveFormulaError(error));
  } finally {
    dispatch(endTask());
  }
};

// ------------------------------------
// Update League Formula
// ------------------------------------
export const UPDATE_FORMULA_REQUEST = `${STORE_KEY}/UPDATE_FORMULA_REQUEST`;
export const UPDATE_FORMULA_SUCCESS = `${STORE_KEY}/UPDATE_FORMULA_SUCCESS`;
export const UPDATE_FORMULA_ERROR = `${STORE_KEY}/UPDATE_FORMULA_ERROR`;

export const updateFormulaRequest = formula => ({
  type: UPDATE_FORMULA_REQUEST,
  payload: { formula },
});
export const updateFormulaSuccess = (newId, formula) => ({
  type: UPDATE_FORMULA_SUCCESS,
  payload: { newId, formula },
});
export const updateFormulaError = error => ({
  type: UPDATE_FORMULA_ERROR,
  payload: { error },
});

actions[UPDATE_FORMULA_ERROR] = (state, { payload }) => ({ ...state,
  error: payload.error,
});

export const updateFormula = formula => async (dispatch, getState, { api }) => {
  try {
    dispatch(beginTask());
    dispatch(updateFormulaRequest(formula));
    
    const state = getState();
    const { id } = selectParams(state);
    
    const response = await api.formula.update({ id, formula });

    dispatch(updateFormulaSuccess(response.formula.id, formula));
    dispatch(goToView(CUSTOM_LEAGUE_VIEW));
  } catch (error) {
    console.error(error);
    dispatch(updateFormulaError(error));
  } finally {
    dispatch(endTask());
  }
};

// ------------------------------------
// Load Formula Options
// ------------------------------------
export const LOAD_FORMULA_OPTIONS_REQUEST = `${STORE_KEY}/LOAD_FORMULA_OPTIONS_REQUEST`;
export const LOAD_FORMULA_OPTIONS_SUCCESS = `${STORE_KEY}/LOAD_FORMULA_OPTIONS_SUCCESS`;
export const LOAD_FORMULA_OPTIONS_ERROR = `${STORE_KEY}/LOAD_FORMULA_OPTIONS_ERROR`;

export const loadFormulaOptionsRequest = () => ({ type: LOAD_FORMULA_OPTIONS_REQUEST });
export const loadFormulaOptionsSuccess = options => ({
  type: LOAD_FORMULA_OPTIONS_SUCCESS,
  payload: { options },
});
export const loadFormulaOptionsError = error => ({
  type: LOAD_FORMULA_OPTIONS_ERROR,
  payload: { error },
});

actions[LOAD_FORMULA_OPTIONS_SUCCESS] = (state, { payload: { options } }) => ({ ...state,
  options,
});
actions[LOAD_FORMULA_OPTIONS_ERROR] = (state, { payload: { error } }) => ({ ...state,
  error,
});

export const loadFormulaOptions = async (dispatch, getState, { api }) => {
  try {
    const optionsExist = !!Object.keys(selectFormulaOptions(getState())).length;
    if (optionsExist) return Promise.resolve();

    dispatch(loadFormulaOptionsRequest());
    const options = await api.formula.getOptions();

    return dispatch(loadFormulaOptionsSuccess(options.formula_options));
  } catch (error) {
    return dispatch(loadFormulaOptionsError(error));
  }
};



// WEBPACK FOOTER //
// ./src/store/reducers/formula.js